
import React from "react";
import { Utils } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { ConfirmDialog, Siren } from "../../../../shared/ConfirmDialog";
import { ButtonManageRef, MessageSchemesIcons, Scheme } from "@rivtn/noseur";
import { BaseService, CountryService, CurrencyService, ICountry, ICurrency, ILanguage, IManagedTransaction, IPlatform, IProfile, ITransaction, LanguageService, PlatformService, SanitizedResponse, TransactionService } from "../../../../../services";
import { Elements } from "../../../../shared/Elements";

export interface CreateTransactionProps {
    profile: IProfile;
    onComplete: (redirectUrl: string) => void;
}

export function CreateTransaction(props: CreateTransactionProps) {
    
    let dialogErrorRef: any;
    const profile = props.profile;
    const countryService = CountryService.getInstance();
    const platformService = PlatformService.getInstance();
    const languageService = LanguageService.getInstance();
    const currencyService = CurrencyService.getInstance();
    const transactionService = TransactionService.getInstance();
    const labels = i18nManager.Labels.dashboard.ploutous.transactions.dialogs.initiate;
    let transaction: ITransaction = {
        amount: 10,
        country: "NGA",
        currency: "NGN",
        msisdn: profile.phone,
        language: "en_UK",
        service_code: "janus-geminus",
        reference: Utils.randomString(15),
        description: `Test payment by ${profile.first_name}`,
        payer_account_number: profile.external_id?.replaceAll("-", "_"),
        redirects_urls: `${window.location.origin}${window.location.pathname}`,
        configuration: {
            auto_redirect_wait_timeout: 15,
        },
        pay: {
            amount: 10000,
            currency: "NGN"
        },
        payer: {
            email: profile.email,
            last_name: profile.last_name,
            first_name: profile.first_name,
            account_number: profile.external_id.replaceAll("-", "_")
        },
        redirects: {
            success: `${window.location.origin}${window.location.pathname}`,
            failure: `${window.location.origin}${window.location.pathname}`,
            partial: `${window.location.origin}${window.location.pathname}`,
        },
        payment_methods: [
            "CARD",
            "USSD",
            "MOBILE_MONEY",
            "BANK_TRANSFER",
        ],
    } as any as ITransaction;

    const mainDialog = ConfirmDialog({
        closable: true,
        title: labels.title,
        style: { minWidth: 950 },
        content: (<div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            {Elements.buildConfirmDialogForm(labels.main_form, (name, value, searching) => {
                dialogErrorRef.innerText = " ";
                if (!searching) {
                    (transaction as any)[name] = value;
                    if (name === "payer_account_number") {
                        (transaction as any).payer.account_number = value;
                    } else if (name === "redirects_urls") {
                        const urls = value.split(",");
                        transaction.redirects.success = urls[0];
                        transaction.redirects.failure = (urls.length > 1 ? urls[1] : urls[0]);
                        transaction.redirects.partial = (urls.length > 2 ? urls[2] : urls[0]);
                    }
                }
                if (name === "service_code") {
                    platformService.queryPlatforms({ name__like: value.value }, true).then(({ apiResponse }: SanitizedResponse<IPlatform>) => {
                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((platform: IPlatform) => {
                            return {
                                "label": platform.name,
                                "value": platform.platform_id
                            };
                        }));
                    }).catch(BaseService.reportError);
                } else if (name === "country") {
                    countryService.queryCountries({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((country) => ({ label: country.name, value: country.code })));
                    }).catch(BaseService.reportError);
                } else if (name === "language") {
                    languageService.queryLanguages({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ILanguage>) => {
                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((language) => ({ label: language.name, value: language.code })));
                    }).catch(BaseService.reportError);
                }
                return false;
            }, transaction)}
            <div ref={(r) => { dialogErrorRef = r; }} className="error"> </div>
        </div>),
        rightContent: (<div style={{ flex: 1, width: 450 }} className="add-new-role-permission">
            {Elements.buildConfirmDialogForm(labels.amounts_form.curr_amount, (name, value, searching) => {
                dialogErrorRef.innerText = " ";
                if (!searching) {
                    (transaction as any)[name] = value;
                    if (name === "currency") {
                        transaction.pay.currency = value;
                    } else if (name === "amount") {
                        transaction.pay.amount = parseInt(value) * 1000;
                    }
                }
                if (name === "currency") {
                    currencyService.queryCurrencies({ name: value.value }).then(({ apiResponse }: SanitizedResponse<ICurrency>) => {
                        value.manageRef?.changeOptions(apiResponse.data!.content?.map((currency) => ({ label: `${currency.name} - ${currency.code}`, value: currency.code })));
                    }).catch(BaseService.reportError);
                }
                return false;
            }, transaction, undefined, "row two")}
            {Elements.buildConfirmDialogForm(labels.amounts_form.percentages, (name, value) => {
                dialogErrorRef.innerText = " ";
                (transaction.pay as any)[name] = parseInt(value ? value : 0);
                return false;
            }, transaction, undefined, "row")}
            {Elements.buildConfirmDialogForm(labels.amounts_form.taxes, (name, value) => {
                dialogErrorRef.innerText = " ";
                (transaction.pay as any)[name] = parseInt(value ? value : 0);
                return false;
            }, transaction, undefined, "row")}

            <div style={{ marginTop: 20 }}>{labels.payment_methods}</div>
            {Elements.buildConfirmDialogForm(labels.payment_methods_form, (name, value) => {
                dialogErrorRef.innerText = " ";
                if (!value && transaction.payment_methods.includes(name)) transaction.payment_methods.splice(transaction.payment_methods.indexOf(name), 1);
                else if (value && !transaction.payment_methods.includes(name)) transaction.payment_methods.push(name);
                return true;
            }, transaction)}

            <div style={{ marginTop: 20 }}>{labels.configurations}</div>
            {Elements.buildConfirmDialogForm(labels.configuration_form, (name, value) => {
                dialogErrorRef.innerText = " ";
                if (!value && (name in transaction.configuration)) delete (transaction.configuration as any)[name];
                else if (value && !(name in transaction.configuration)) (transaction.configuration as any)[name] = value;
                return true;
            }, transaction)}
        </div>),
        onConfirm: (confirmButton?: ButtonManageRef) => {
            dialogErrorRef.innerText = " ";
            const erroMessages: string[] = [];
            if (!transaction.country) erroMessages.push(labels.errors.country_required);
            if (!transaction.pay?.amount) erroMessages.push(labels.errors.amount_required);
            if (!transaction.reference) erroMessages.push(labels.errors.reference_required);
            if (!transaction.pay?.currency) erroMessages.push(labels.errors.currency_required);
            if (!transaction.service_code) erroMessages.push(labels.errors.service_code_required);
            if (!(transaction as any).language) erroMessages.push(labels.errors.language_required);
            if (!(transaction as any).payer?.account_number) erroMessages.push(labels.errors.account_number_required);
            if (!transaction.payment_methods?.length) erroMessages.push(labels.errors.payment_methods_required);
            if (!transaction.redirects?.success || !transaction.redirects?.failure || !transaction.redirects?.partial) erroMessages.push(labels.errors.redirects_urls_required);
            if (erroMessages.length) {
                dialogErrorRef.innerText = erroMessages.join(", ");
                return false;
            }
            if (!transaction.redirects.success.includes("trans_status=")) {
                transaction.redirects.success += (!transaction.redirects?.success.includes("?") ? "?" : "&") + "trans_status=success";
                transaction.redirects.failure += (!transaction.redirects?.failure.includes("?") ? "?" : "&") + "trans_status=failure";
                transaction.redirects.partial += (!transaction.redirects?.partial.includes("?") ? "?" : "&") + "trans_status=partial";
            }
            confirmButton?.setLoadingState(true);
            transactionService.initiateTransaction(transaction).then(({ apiResponse }: SanitizedResponse<IManagedTransaction>) => {
                mainDialog.destroy(() => renderSuccessDialog(apiResponse.data?.url as string));
            }).catch((err: any) => {
                BaseService.reportError(err);
                dialogErrorRef.innerText = err?.errors?.join?.(".\n") ?? err?.message ?? err;
            }).finally(() => {
                confirmButton?.setLoadingState(false);
            });
            return false;
        }
    });
    return mainDialog;

    function renderSuccessDialog(url: string) {
        console.log(url);
        ConfirmDialog({
            desc: url,
            title: labels.success.title,
            cancelLabel: labels.success.copy_to_clipboard,
            confirmLabel: labels.success.visit_payment_link,
            onCancel: () => {
                Utils.copyToClipboard(url, (err: any) => {
                    Siren.alert((err ? labels.success.copy_failed : labels.success.copy_success), (err ? { scheme: Scheme.DANGER, icon: MessageSchemesIcons.INFO } : {}));
                    return true;
                });
            },
            onConfirm: (confirmButton?: ButtonManageRef) => {
                confirmButton?.setLoadingState(true);
                window.location.replace(url);
                return false;
            },
        }).show();
    }
}
